import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LOGO_URL } from "../constants/brand";
import { PageWatermark } from "../components/shared/PageWatermark";
import { FieldLabel } from "../components/shared/FieldHint";

const REF_STORAGE_KEY = "igc_ref_code";

export default function JoinPage() {
  const [searchParams] = useSearchParams();
  const urlRef = (searchParams.get("ref") || "").toUpperCase();
  const managerRef = urlRef || (localStorage.getItem(REF_STORAGE_KEY) || "").toUpperCase();

  useEffect(() => {
    if (urlRef) localStorage.setItem(REF_STORAGE_KEY, urlRef);
  }, [urlRef]);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/join", { ...form, manager_ref: managerRef });
      setSuccess(data);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-auth-bg flex items-center justify-center p-6 relative overflow-hidden">
      <PageWatermark variant="auth" />
      <div className="absolute inset-0 opacity-[0.14]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #d9b44a 0, transparent 45%), radial-gradient(circle at 80% 80%, #d9b44a 0, transparent 45%)" }} />
      <div className="w-full max-w-md bg-auth-surface border border-auth-gold/25 p-10 relative z-10 auth-glow" data-testid="join-card">
        <div className="flex flex-col items-center mb-8">
          <img src={LOGO_URL} alt="Iron Gate Consulting" className="w-24 h-24 object-contain mb-4 auth-glow" />
          <h1 className="font-serif text-2xl text-auth-cream tracking-tight text-center">Become an IGC Affiliate</h1>
          {managerRef ? (
            <p className="text-auth-gold text-sm mt-2" data-testid="join-manager-ref">Joining under manager {managerRef}</p>
          ) : (
            <p className="text-auth-danger text-sm mt-2" data-testid="join-missing-ref">This link is missing a manager referral code.</p>
          )}
        </div>

        {success ? (
          <div data-testid="join-success-message" className="text-center">
            <p className="text-auth-cream text-sm">{success.message}</p>
            <p className="text-auth-gold text-lg font-serif mt-3" data-testid="join-user-id">{success.user_id}</p>
            <Link to="/login" className="text-auth-gold text-sm mt-6 inline-block hover:text-auth-goldHover">Back to login</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="join-form">
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="join-first-name-input" required placeholder="First name" title="Your first name." value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-11" />
              <Input data-testid="join-last-name-input" required placeholder="Last name" title="Your last name." value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-11" />
            </div>
            <Input data-testid="join-email-input" type="email" required placeholder="Email" title="The email you'll use to log in once approved — also where your welcome email will be sent." value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-11" />
            <div className="flex items-center gap-1.5">
              <Input data-testid="join-phone-input" placeholder="Phone" title="Your phone number." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-11 flex-1" />
              <FieldLabel hint="Optional, but helps your manager reach you faster about leads." testId="join-phone-hint" />
            </div>
            <div className="flex items-center gap-1.5">
              <Input data-testid="join-password-input" type="password" required placeholder="Choose a password" title="Pick a password you'll remember — you'll need it to log in once an admin approves your account." value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-auth-surfaceLight border-auth-gold/20 text-auth-cream h-11 flex-1" />
              <FieldLabel hint="You'll use this password to log in once an admin approves your account." testId="join-password-hint" />
            </div>
            {error && <p className="text-auth-danger text-sm" data-testid="join-error">{error}</p>}
            <Button data-testid="join-submit-button" type="submit" disabled={submitting || !managerRef} title="Creates your affiliate account — an IGC admin still needs to approve it before you can log in." className="w-full h-12 bg-auth-gold hover:bg-auth-goldHover text-black font-medium">
              {submitting ? "Submitting..." : "Sign Up"}
            </Button>
            <p className="text-auth-muted text-xs text-center">Your account is created right away but a IGC admin must approve it before you can log in.</p>
          </form>
        )}
      </div>
    </div>
  );
}
